import { z } from "zod";
import {
  BasemapAnnotations,
  BasemapDisplay,
  BasemapExpression,
  BasemapFeatureSetRules,
  BasemapGeometry,
  BasemapName,
  BasemapPaintStep,
  BasemapRasterRules,
  BasemapVariable,
  BasemapWaterBodyRules,
} from "./manifest";

/**
 * Everything in a draft that isn't tiles or features: a manifest's rules without its files, since
 * the publisher forms bundles and names files.
 */
export const BasemapDraftDocument = z
  .strictObject({
    geometry: BasemapGeometry,
    display: BasemapDisplay,
    variables: z.record(BasemapName, BasemapVariable),
    rasters: z.record(
      BasemapName,
      z.intersection(
        BasemapRasterRules,
        z.strictObject({
          finestLevel: z.int().min(9).max(32).meta({
            description:
              "The level new places start at. Raising or lowering detail changes it place by place.",
          }),
        }),
      ),
    ),
    waterBodies: z.record(BasemapName, BasemapWaterBodyRules),
    featureSets: z.record(BasemapName, BasemapFeatureSetRules),
    expressions: z.record(BasemapName, BasemapExpression),
    paintOrder: z.array(BasemapPaintStep).max(64),
    annotations: BasemapAnnotations,
  })
  .meta({
    id: "BasemapDraftDocument",
    description:
      "A draft's plain values: its geometry, rules, variables, paint order, and annotations.",
  });
export type BasemapDraftDocument = z.infer<typeof BasemapDraftDocument>;

/** A cube face, 0 to 5, or 0 on a plane. */
export const BasemapFace = z.int().min(0).max(5);

/** One tile's place: its raster, face, level, and column and row at that level. */
export const BasemapTileAddress = z
  .strictObject({
    raster: BasemapName,
    face: BasemapFace,
    level: z.int().min(9).max(32),
    x: z.int().min(0),
    y: z.int().min(0),
  })
  .meta({ id: "BasemapTileAddress" });
export type BasemapTileAddress = z.infer<typeof BasemapTileAddress>;
